/* cl-hero3d v2 (Claude, 19.10.2026) – Schichtaufbau einer Rohrisolierung fuer den Startseitenkopf.
   Rohr -> Rohrschale Steinwolle (alukaschiert) -> Blechmantel; danach waechst die Daemmung durch die
   Daemmdicken aus data-steps (mm). Masse: 1 Modelleinheit = 100 mm, Rohr-Aussendurchmesser data-pipe (mm).
   Keine Marken, reines Technikschema. Ohne WebGL: statisches Bild aus <template>.
   v2: transparenter Hintergrund, Shader vorkompiliert, Zeitschritt begrenzt (kein Springen),
       Rendering pausiert ausserhalb des Sichtbereichs, Pixeldichte max. 1,5. */
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js';

function tex(THREE, w, h, draw) {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  draw(c.getContext('2d'), w, h);
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
function fallback(root) {
  root.classList.add('is-fallback');
  const t = root.querySelector('template'); if (t) root.appendChild(t.content.cloneNode(true));
}
const ease = x => { x = Math.min(Math.max(x, 0), 1); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; };
const easeOut = x => 1 - Math.pow(1 - Math.min(Math.max(x, 0), 1), 3);

async function init(root) {
  const canvas = root.querySelector('canvas');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const steps = (root.dataset.steps || '20,30,40,50').split(',').map(parseFloat).filter(n => n >= 10 && n <= 60);
  if (!steps.length) steps.push(30);
  const pipeR = Math.min(Math.max(parseFloat(root.dataset.pipe) || 60, 20), 90) / 200;
  const badge = root.querySelector('[data-thickness]');
  let THREE;
  try {
    const test = document.createElement('canvas');
    if (!(test.getContext('webgl2') || test.getContext('webgl'))) throw new Error('kein WebGL');
    THREE = await import(root.dataset.three || THREE_URL);
  } catch (e) { fallback(root); return; }

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);

  scene.add(new THREE.HemisphereLight(0xffffff, 0xd9d6cf, 1.15));
  const key = new THREE.DirectionalLight(0xffffff, 2.2); key.position.set(4, 6, 5); scene.add(key);
  const rim = new THREE.DirectionalLight(0xdfe8f5, 1.0); rim.position.set(-6, 3, -4); scene.add(rim);

  // Umgebung nur fuer Metallreflexe (nicht sichtbar)
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new THREE.Scene(); envScene.background = new THREE.Color(0xf2f1ee);
  const lamp = new THREE.Mesh(new THREE.PlaneGeometry(6, 2), new THREE.MeshBasicMaterial({ color: 0xffffff }));
  lamp.position.set(0, 4, 2); lamp.lookAt(0, 0, 0); envScene.add(lamp);
  const lamp2 = lamp.clone(); lamp2.position.set(-4, 1, -3); lamp2.lookAt(0, 0, 0); envScene.add(lamp2);
  scene.environment = pmrem.fromScene(envScene, 0.04).texture; pmrem.dispose();

  // Texturen (klein gehalten, schnell erzeugt)
  const brushed = tex(THREE, 512, 32, (g, w, h) => {
    g.fillStyle = '#c4c8cc'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 1100; i++) { g.fillStyle = `rgba(255,255,255,${Math.random() * 0.16})`; g.fillRect(Math.random() * w, 0, 0.6, h); g.fillStyle = `rgba(60,60,60,${Math.random() * 0.1})`; g.fillRect(Math.random() * w, 0, 0.5, h); }
  });
  const wool = tex(THREE, 256, 256, (g, w, h) => {
    g.fillStyle = '#c9ad6a'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 2600; i++) {
      const x = Math.random() * w, y = Math.random() * h, l = 3 + Math.random() * 9, a = Math.random() * Math.PI;
      g.strokeStyle = Math.random() < 0.5 ? 'rgba(110,82,32,0.55)' : 'rgba(240,220,160,0.6)';
      g.lineWidth = 0.5 + Math.random() * 0.8; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l); g.stroke();
    }
  });
  const foil = tex(THREE, 128, 128, (g, w, h) => {
    g.fillStyle = '#cfd3d7'; g.fillRect(0, 0, w, h); g.strokeStyle = 'rgba(150,155,160,0.35)'; g.lineWidth = 1;
    for (let i = 0; i < w; i += 8) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 20, h); g.stroke(); g.beginPath(); g.moveTo(i + 20, 0); g.lineTo(i, h); g.stroke(); }
  });
  foil.repeat.set(4, 2);

  const steelMat = new THREE.MeshStandardMaterial({ color: 0x5d6268, metalness: 0.85, roughness: 0.38 });
  const steelInner = new THREE.MeshStandardMaterial({ color: 0x2c2f33, metalness: 0.6, roughness: 0.6, side: THREE.BackSide });
  const woolMat = new THREE.MeshStandardMaterial({ map: wool, roughness: 0.95, metalness: 0, side: THREE.DoubleSide });
  const woolBack = new THREE.MeshStandardMaterial({ map: wool, roughness: 0.95, metalness: 0, side: THREE.BackSide });
  const foilMat = new THREE.MeshStandardMaterial({ map: foil, metalness: 0.55, roughness: 0.35 });
  const sheetMat = new THREE.MeshStandardMaterial({ map: brushed, metalness: 0.92, roughness: 0.28 });

  const group = new THREE.Group(); scene.add(group);
  const AX = new THREE.Euler(0, 0, Math.PI / 2); // Zylinderachse entlang X

  // 1 Rohr
  const rP = pipeR, pipeLen = 4.4;
  const pipe = new THREE.Group();
  const pipeOuter = new THREE.Mesh(new THREE.CylinderGeometry(rP, rP, pipeLen, 64, 1, true), steelMat);
  const pipeIn = new THREE.Mesh(new THREE.CylinderGeometry(rP * 0.88, rP * 0.88, pipeLen, 48, 1, true), steelInner);
  [pipeOuter, pipeIn].forEach(m => { m.rotation.copy(AX); pipe.add(m); });
  const endRing = new THREE.Mesh(new THREE.RingGeometry(rP * 0.88, rP, 64), steelMat);
  endRing.position.x = pipeLen / 2; endRing.rotation.y = Math.PI / 2; pipe.add(endRing);
  group.add(pipe);

  // 2 Rohrschale: zwei Halbschalen; Aussenradius zur Laufzeit veraenderbar (Stirnflaechen + Fugen als eigene Geometrie)
  const L = 2.9, SEG = 48, rIn = rP + 0.004, n = SEG + 1;
  function halfShell(ts) {
    const half = new THREE.Group();
    const outer = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, L, SEG, 1, true, ts, Math.PI), foilMat);
    const inner = new THREE.Mesh(new THREE.CylinderGeometry(rIn, rIn, L, SEG, 1, true, ts, Math.PI), woolBack);
    const count = 4 * n + 8;
    const pos = new Float32Array(count * 3), uv = new Float32Array(count * 2), idx = [];
    const put = (i, x, y, z, u, v) => { pos.set([x, y, z], i * 3); uv.set([u, v], i * 2); };
    for (let e = 0; e < 2; e++) {
      const y = e === 0 ? L / 2 : -L / 2, b = e * 2 * n;
      for (let i = 0; i < n; i++) { const th = ts + Math.PI * i / SEG; put(b + i, Math.sin(th) * rIn, y, Math.cos(th) * rIn, 0, 0); }
      for (let i = 0; i < SEG; i++) { const a = b + i, c = b + n + i; if (e === 0) idx.push(a, c, a + 1, a + 1, c, c + 1); else idx.push(a, a + 1, c, a + 1, c + 1, c); }
    }
    const sb = 4 * n;
    for (let s = 0; s < 2; s++) { const b = sb + s * 4; idx.push(b, b + 1, b + 2, b + 2, b + 1, b + 3); }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setIndex(idx);
    const caps = new THREE.Mesh(geo, woolMat);
    function setOuter(r) {
      outer.scale.set(r, 1, r);
      for (let e = 0; e < 2; e++) {
        const y = e === 0 ? L / 2 : -L / 2, b = e * 2 * n;
        for (let i = 0; i < n; i++) {
          const th = ts + Math.PI * i / SEG, si = Math.sin(th), co = Math.cos(th);
          put(b + i, si * rIn, y, co * rIn, si * rIn * 1.4 + 0.5, co * rIn * 1.4 + 0.5);
          put(b + n + i, si * r, y, co * r, si * r * 1.4 + 0.5, co * r * 1.4 + 0.5);
        }
      }
      for (let s = 0; s < 2; s++) {
        const th = ts + s * Math.PI, si = Math.sin(th), co = Math.cos(th), b = sb + s * 4;
        put(b, si * rIn, L / 2, co * rIn, 0, 0); put(b + 1, si * rIn, -L / 2, co * rIn, 0, 2);
        put(b + 2, si * r, L / 2, co * r, (r - rIn) * 2, 0); put(b + 3, si * r, -L / 2, co * r, (r - rIn) * 2, 2);
      }
      geo.attributes.position.needsUpdate = true; geo.attributes.uv.needsUpdate = true;
    }
    setOuter(rIn + steps[0] / 100); geo.computeVertexNormals();
    [outer, inner, caps].forEach(m => half.add(m));
    half.rotation.copy(AX);
    const wrap = new THREE.Group(); wrap.add(half); wrap.position.x = -0.35; group.add(wrap);
    return { wrap, setOuter };
  }
  const shells = [halfShell(0), halfShell(Math.PI)];

  // 3 Blechmantel mit Laengsueberlappung und Sicken; wird radial mitskaliert
  const rJ0 = 0.635, jLen = 1.7;
  const jacket = new THREE.Group();
  const jMesh = new THREE.Mesh(new THREE.CylinderGeometry(rJ0, rJ0, jLen, 96, 1, true, 0.12, Math.PI * 2 - 0.02), sheetMat);
  const lap = new THREE.Mesh(new THREE.CylinderGeometry(rJ0 + 0.012, rJ0 + 0.012, jLen, 24, 1, true, -0.02, 0.2), sheetMat);
  const beadGeo = new THREE.TorusGeometry(rJ0 + 0.004, 0.012, 8, 96);
  const b1 = new THREE.Mesh(beadGeo, sheetMat); b1.rotation.x = Math.PI / 2; b1.position.y = jLen / 2 - 0.06;
  const b2 = b1.clone(); b2.position.y = -jLen / 2 + 0.06;
  const inside = new THREE.Mesh(new THREE.CylinderGeometry(rJ0 - 0.004, rJ0 - 0.004, jLen, 64, 1, true), new THREE.MeshStandardMaterial({ color: 0x9aa0a6, metalness: 0.8, roughness: 0.45, side: THREE.BackSide }));
  [jMesh, lap, b1, b2, inside].forEach(m => jacket.add(m));
  jacket.rotation.copy(AX); jacket.rotation.y = Math.PI;
  const jWrap = new THREE.Group(); jWrap.add(jacket); group.add(jWrap);

  // weicher Bodenschatten
  const shadowTex = tex(THREE, 256, 256, (g, w, h) => { const gr = g.createRadialGradient(w / 2, h / 2, 2, w / 2, h / 2, w / 2); gr.addColorStop(0, 'rgba(40,36,30,0.26)'); gr.addColorStop(0.55, 'rgba(40,36,30,0.10)'); gr.addColorStop(1, 'rgba(40,36,30,0)'); g.fillStyle = gr; g.fillRect(0, 0, w, h); });
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(4.8, 1.7), new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false }));
  shadow.rotation.x = -Math.PI / 2; group.add(shadow);

  group.rotation.y = -0.62;

  // Beschriftungen
  const labels = [...root.querySelectorAll('[data-anchor]')];
  const anchors = { pipe: new THREE.Vector3(1.95, rP, 0), wool: new THREE.Vector3(0.8, 0.6, 0), jacket: new THREE.Vector3(-0.9, 0.64, 0) };
  const tmp = new THREE.Vector3();
  let cw = 1, ch = 1;
  const maxR = rIn + Math.max(...steps) / 100 + 0.04;

  function resize() {
    cw = canvas.clientWidth || 1; ch = canvas.clientHeight || 1;
    renderer.setSize(cw, ch, false);
    camera.aspect = cw / ch;
    const narrow = camera.aspect < 1.45, s = Math.max(1, maxR / 0.66);
    camera.fov = 30;
    camera.position.set((narrow ? 1.1 : 1.4) * s, (narrow ? 1.6 : 2.0) * s, (narrow ? 5.5 : 5.9) * s);
    camera.lookAt(narrow ? -0.05 : 0.05, narrow ? 0.18 : 0.12, 0);
    root.classList.toggle('is-narrow', cw < 520);
    camera.updateProjectionMatrix();
    if (!running) draw(t);
  }

  // Zeitplan: 0.2–1.3 s Schalen schliessen, 1.2–2.3 s Mantel faehrt ein, ab 2.8 s Daemmdicken-Zyklus
  const HOLD = 2.2, MORPH = 1.1, P = HOLD + MORPH, N = steps.length;
  let built = false, shownMM = -1;
  function thicknessAt(t) {
    if (reduce || N < 2) return steps[Math.min(1, N - 1)];
    const tc = t - 2.8; if (tc < 0) return steps[0];
    const k = Math.floor(tc / P), loc = tc - k * P, cur = steps[k % N], nxt = steps[(k + 1) % N];
    return loc < HOLD ? cur : cur + (nxt - cur) * ease((loc - HOLD) / MORPH);
  }
  function update(t) {
    const a = easeOut((t - 0.2) / 1.1), b = easeOut((t - 1.2) / 1.1);
    shells[0].wrap.position.y = (1 - a) * 1.6; shells[1].wrap.position.y = -(1 - a) * 1.6;
    shells[0].wrap.visible = shells[1].wrap.visible = a > 0.001;
    jWrap.position.x = -0.95 - (1 - b) * 3.4; jWrap.visible = b > 0.001;
    const mm = thicknessAt(t), rO = rIn + mm / 100, rJ = rO + 0.035;
    shells.forEach(s => s.setOuter(rO));
    const k = rJ / rJ0; jacket.scale.set(k, 1, k);
    anchors.wool.y = rO; anchors.jacket.y = rJ;
    shadow.position.y = -rJ - 0.02;
    group.position.y = 0.02;
    group.rotation.y = -0.62 + (reduce ? 0 : Math.sin(t * 0.3) * 0.1);
    if (!built && b > 0.98) { built = true; root.classList.add('is-built'); }
    const r = Math.round(mm);
    if (badge && r !== shownMM) { shownMM = r; badge.textContent = r + ' mm'; }
  }
  function placeLabels() {
    for (const l of labels) {
      const a = anchors[l.dataset.anchor]; if (!a) continue;
      tmp.copy(a); group.localToWorld(tmp); tmp.project(camera);
      l.style.transform = `translate3d(${((tmp.x * 0.5 + 0.5) * cw).toFixed(1)}px,${((-tmp.y * 0.5 + 0.5) * ch).toFixed(1)}px,0)`;
    }
  }
  function draw(time) { update(time); renderer.render(scene, camera); placeLabels(); }

  let t = reduce ? 99 : (parseFloat(root.dataset.fixedT) || 0), last = 0, running = false, loop = 0;
  const fixed = root.dataset.fixedT != null;
  function frame(now, id) {
    if (!running || id !== loop) return;
    t += last ? Math.min((now - last) / 1000, 0.1) : 0; // begrenzter Schritt: kein Springen nach Ladespitzen
    last = now; draw(t);
    requestAnimationFrame(n => frame(n, id));
  }
  function start() { if (running || reduce || fixed) return; running = true; last = 0; const id = ++loop; requestAnimationFrame(n => frame(n, id)); }
  function stop() { running = false; }

  new ResizeObserver(resize).observe(canvas); resize();
  // Shader und Texturen vorab auf die GPU, dann erst sichtbar machen und starten
  update(t); renderer.compile(scene, camera); renderer.render(scene, camera); placeLabels();
  requestAnimationFrame(() => {
    root.classList.add('is-ready');
    new IntersectionObserver(e => { e[e.length - 1].isIntersecting ? start() : stop(); }).observe(root);
  });
}

document.querySelectorAll('[data-cl-hero3d]').forEach(el => init(el));
